import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, UserCog, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const emptyForm = { email: "", name: "", password: "", role: "user" };

export default function UsersAdmin() {
    const { user: current } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [resetting, setResetting] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", email: "", role: "user" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/users");
            setUsers(res.data);
        } catch {
            toast.error("Erro ao carregar usuários");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm(emptyForm); setOpen(true); };

    const save = async () => {
        if (!form.email.trim() || !form.name.trim() || !form.password) {
            toast.error("Preencha todos os campos");
            return;
        }
        if (form.password.length < 6) {
            toast.error("Senha deve ter no mínimo 6 caracteres");
            return;
        }
        try {
            await api.post("/admin/users", form);
            toast.success("Usuário criado!");
            setOpen(false);
            load();
        } catch (err) {
            const msg = err.response?.data?.detail || "Erro ao criar usuário";
            toast.error(typeof msg === "string" ? msg : "Erro ao criar usuário");
        }
    };

    const doReset = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error("Senha deve ter no mínimo 6 caracteres");
            return;
        }
        try {
            await api.post(`/admin/users/${resetting.id}/reset-password`, { password: newPassword });
            toast.success("Senha atualizada!");
            setResetting(null);
            setNewPassword("");
        } catch {
            toast.error("Erro ao resetar senha");
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/admin/users/${id}`);
            toast.success("Usuário removido");
            load();
        } catch (err) {
            const msg = err.response?.data?.detail || "Erro ao remover";
            toast.error(typeof msg === "string" ? msg : "Erro ao remover");
        } finally {
            setConfirmDelete(null);
        }
    };

    const openEdit = (u) => {
        setEditing(u);
        setEditForm({ name: u.name, email: u.email, role: u.role });
    };

    const saveEdit = async () => {
        if (!editForm.name.trim() || !editForm.email.trim()) {
            toast.error("Preencha nome e email");
            return;
        }
        try {
            await api.put(`/admin/users/${editing.id}`, editForm);
            toast.success("Usuário atualizado!");
            setEditing(null);
            load();
        } catch (err) {
            const msg = err.response?.data?.detail || "Erro ao atualizar";
            toast.error(typeof msg === "string" ? msg : "Erro ao atualizar");
        }
    };

    return (
        <div className="space-y-6" data-testid="users-admin-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground flex items-center gap-3">
                        <UserCog className="w-7 h-7 text-primary" /> Usuários
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">Gerencie quem pode acessar o sistema</p>
                </div>
                <Button onClick={openCreate} data-testid="btn-new-user" className="gap-2">
                    <Plus className="w-4 h-4" /> Novo usuário
                </Button>
            </div>

            <Card>
                <CardContent className="p-6">
                    {loading ? (
                        <div className="text-muted-foreground py-8 text-center">Carregando...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-muted-foreground border-b border-border">
                                        <th className="py-3 px-3 font-semibold">Nome</th>
                                        <th className="py-3 px-3 font-semibold">Email</th>
                                        <th className="py-3 px-3 font-semibold">Perfil</th>
                                        <th className="py-3 px-3 font-semibold text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="users-table-body">
                                    {users.map((u) => (
                                        <tr key={u.id} className="border-b border-border row-hover">
                                            <td className="py-3 px-3 font-medium">
                                                {u.name}
                                                {u.id === current?.id && (
                                                    <Badge variant="secondary" className="ml-2 text-[10px]">Você</Badge>
                                                )}
                                            </td>
                                            <td className="py-3 px-3 text-muted-foreground">{u.email}</td>
                                            <td className="py-3 px-3">
                                                <Badge variant={u.role === "admin" ? "default" : "secondary"} className="uppercase text-[10px]">
                                                    {u.role === "admin" ? "Admin" : "Usuário"}
                                                </Badge>
                                            </td>
                                            <td className="py-3 px-3 text-right">
                                                <div className="flex gap-2 justify-end">
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} title="Editar dados" data-testid={`btn-edit-user-${u.id}`}>
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => { setResetting(u); setNewPassword(""); }} title="Resetar senha" data-testid={`btn-reset-${u.id}`}>
                                                        <KeyRound className="w-4 h-4" />
                                                    </Button>
                                                    {u.id !== current?.id && (
                                                        <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(u)} title="Remover" data-testid={`btn-delete-user-${u.id}`}>
                                                            <Trash2 className="w-4 h-4 text-destructive" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="user-dialog">
                    <DialogHeader>
                        <DialogTitle>Novo usuário</DialogTitle>
                        <DialogDescription>Cadastre um novo acesso ao sistema</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Nome</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-user-name" />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-user-email" />
                        </div>
                        <div>
                            <Label>Senha (mínimo 6 caracteres)</Label>
                            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="input-user-password" />
                        </div>
                        <div>
                            <Label>Perfil</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger data-testid="select-user-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="user">Usuário (operador)</SelectItem>
                                    <SelectItem value="admin">Administrador</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} data-testid="btn-save-user">Criar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent data-testid="edit-user-dialog">
                    <DialogHeader>
                        <DialogTitle>Editar usuário</DialogTitle>
                        <DialogDescription>Altere os dados de <strong>{editing?.name}</strong></DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Nome</Label>
                            <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="input-edit-name" />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="input-edit-email" />
                        </div>
                        <div>
                            <Label>Perfil</Label>
                            <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                                <SelectTrigger data-testid="select-edit-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="user">Usuário (operador)</SelectItem>
                                    <SelectItem value="admin">Administrador</SelectItem>
                                </SelectContent>
                            </Select>
                            {editing?.id === current?.id && (
                                <p className="text-xs text-muted-foreground mt-2">Você não pode remover seu próprio perfil de administrador.</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                        <Button onClick={saveEdit} data-testid="btn-save-edit-user">Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!resetting} onOpenChange={(v) => !v && setResetting(null)}>
                <DialogContent data-testid="reset-password-dialog">
                    <DialogHeader>
                        <DialogTitle>Resetar senha</DialogTitle>
                        <DialogDescription>Defina uma nova senha para <strong>{resetting?.name}</strong></DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label>Nova senha (mínimo 6 caracteres)</Label>
                        <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="input-new-password" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResetting(null)}>Cancelar</Button>
                        <Button onClick={doReset} data-testid="btn-confirm-reset">Atualizar senha</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
                        <AlertDialogDescription>
                            O usuário <strong>{confirmDelete?.name}</strong> perderá o acesso ao sistema.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(confirmDelete.id)} data-testid="btn-confirm-delete-user">Remover</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
